import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaBoxes,
  FaFileAlt,
  FaClipboardCheck,
  FaUsers,
  FaPlus,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaRoute,
  FaCheckCircle,
  FaPlay,
  FaQrcode,
} from 'react-icons/fa';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import { useDashboardStats, useIssueStats } from '../dry/hooks';
import { useHttp } from '../dry/hooks';
import { useAuth } from '../contexts/AuthContext';
import { isManager, isAdmin } from '../utils/permissions';
import { JobInstance, DashboardStats } from '../types';
import { useLabel } from '../i18n/useLabel';
import { useDashboardScope } from '../hooks/useDashboardScope';
import ScopeSwitcher from '../components/dashboard/ScopeSwitcher';
import ExceptionsBanner from '../components/dashboard/ExceptionsBanner';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const http = useHttp();
  const { user } = useAuth();
  const isMgr = isManager(user?.role) || isAdmin(user?.role);
  const scope = useDashboardScope();
  // Platform-noun labels for tile titles and section headings.
  const labelAssetsPlural = useLabel('asset', { form: 'plural' });
  const labelFormsPlural = useLabel('form', { form: 'plural' });
  const labelSubsPlural = useLabel('submission', { form: 'plural' });

  // Using React Query hook for dashboard stats
  const { data: stats, isLoading, error } = useDashboardStats();

  // "Open Issues" = everything still needing attention (open + acknowledged
  // + in_progress). Resolved/closed/spam are excluded.
  const { data: issueStats } = useIssueStats();
  const openIssuesCount =
    (issueStats?.open_issues || 0) +
    (issueStats?.acknowledged_issues || 0) +
    (issueStats?.in_progress_issues || 0);

  // The worker's open jobs — today AND overdue (date <= today, not yet done), so a
  // past-due backlog is visible instead of a misleadingly empty "today only" list.
  // The API returns newest-first, so today's jobs sit above overdue ones.
  const [todayJobs, setTodayJobs] = useState<JobInstance[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const jobNow = new Date();
  const todayStr = `${jobNow.getFullYear()}-${String(jobNow.getMonth() + 1).padStart(2, '0')}-${String(jobNow.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const params: Record<string, string | number> = { status: 'pending,in_progress', to: todayStr, limit: 10 };
        if (scope.siteIdsParam) params.site_ids = scope.siteIdsParam;
        const resp = await http.get<any>('/job-instances', { params });
        setTodayJobs(resp.data?.data || []);
      } catch {
        // Jobs feature may not be set up yet — silently ignore
      } finally {
        setLoadingJobs(false);
      }
    };
    loadJobs();
  }, [scope.siteIdsParam]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  // Provide default values if data is not yet loaded
  const dashboardData: DashboardStats = stats || {
    total_assets: 0,
    total_forms: 0,
    total_submissions: 0,
    total_users: 0,
    submissions_this_month: 0,
    submissions_last_month: 0,
    active_assets: 0,
    pending_approvals: 0,
    recent_submissions: [],
    popular_forms: [],
    asset_utilization: [],
  };

  // Carry the current site scope into the stat-tile drill-down links, same
  // pattern as TodayScheduleCard's scopeQuery — `&` when the target already
  // has a query string, `?` when it doesn't.
  const scopeSiteIds = scope.siteIdsParam ? encodeURIComponent(scope.siteIdsParam) : '';

  const statCards = [
    {
      title: `Total ${labelAssetsPlural}`,
      value: dashboardData?.total_assets || 0,
      icon: FaBoxes,
      color: 'bg-blue-500',
      link: scopeSiteIds ? `/assets?site_ids=${scopeSiteIds}` : '/assets',
    },
    {
      title: `Total ${labelFormsPlural}`,
      value: dashboardData?.total_forms || 0,
      icon: FaFileAlt,
      color: 'bg-green-500',
      link: '/forms',
    },
    {
      title: labelSubsPlural,
      value: dashboardData?.total_submissions || 0,
      icon: FaClipboardCheck,
      color: 'bg-purple-500',
      link: scopeSiteIds ? `/submissions?site_ids=${scopeSiteIds}` : '/submissions',
    },
    {
      title: 'Open Issues',
      value: openIssuesCount,
      icon: FaExclamationTriangle,
      color: 'bg-red-500',
      link: scopeSiteIds ? `/issues?status=open&site_ids=${scopeSiteIds}` : '/issues?status=open',
    },
    {
      // Renamed from "Users" — the count excludes the viewer, so
      // "Team members" reads more accurately and avoids the "I'm a
      // user, why does it say 0?" confusion (F3).
      title: 'Team members',
      value: dashboardData?.total_users || 0,
      icon: FaUsers,
      color: 'bg-orange-500',
      link: '/users',
    },
  ];

  // Tenant-level tiles (Total Forms, Team members) have no site dimension and
  // are admin-console concerns, not day-to-day work — drop them for a scoped
  // manager (scope.hasCeiling) so the grid only shows what the ceiling covers.
  const visibleStatCards = scope.hasCeiling
    ? statCards.filter(
        (stat) => stat.title !== `Total ${labelFormsPlural}` && stat.title !== 'Team members',
      )
    : statCards;

  // Detect a fresh tenant — no assets, no forms, no submissions —
  // and only nudge admins (not employees) toward Setup Hub. Workers
  // shouldn't see "Continue setup" since they don't own that work.
  // (F1 hero copy + F2 chain-aware CTAs.)
  const isSetupIncomplete = isAdmin(user?.role) &&
    (dashboardData?.total_assets || 0) === 0 &&
    (dashboardData?.total_forms || 0) === 0 &&
    (dashboardData?.total_submissions || 0) === 0;

  const submissionChange = dashboardData
    ? ((dashboardData.submissions_this_month - dashboardData.submissions_last_month) /
        (dashboardData.submissions_last_month || 1)) *
      100
    : 0;

  return (
    <div className="space-y-6">
      {/* Header — copy + CTAs adapt to first-login / setup-incomplete state.
          ScopeSwitcher sits top-right; it renders null for users with no
          site ceiling (non-managers, or managers with unrestricted access). */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {isSetupIncomplete
              ? "Welcome to ChecksAndTicks. Let's get your tenant set up — start with locations, asset types, and forms."
              : "Welcome back! Here's what's happening today."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ScopeSwitcher />
          {isSetupIncomplete ? (
            // Fresh tenant — point at Setup Hub instead of one-shot
            // create CTAs. The chain matters more than ad-hoc adds.
            <Button onClick={() => navigate('/setup')}>
              <FaPlus className="mr-2" />
              Continue setup →
            </Button>
          ) : (
            isMgr && (
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => navigate('/assets/new')}>
                  <FaPlus className="mr-2" />
                  New Asset
                </Button>
                <Button onClick={() => navigate('/forms/new')}>
                  <FaPlus className="mr-2" />
                  New Form
                </Button>
              </div>
            )
          )}
        </div>
      </div>

      {/* Exceptions-first strip — rolls up overdue/approval/flagged items
          within the current scope. Renders directly under the header, above
          everything else, so what needs attention is the first thing seen. */}
      <ExceptionsBanner siteIdsParam={scope.siteIdsParam} />

      {/* Today's Jobs — the "Today hero", promoted above the fold so a
          worker's own open work is the first substantive card on the page. */}
      {!loadingJobs && (todayJobs.length > 0 || !isMgr) && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FaRoute className="text-blue-500" />
              Your Jobs
            </h2>
            <button
              onClick={() => navigate('/jobs')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View All →
            </button>
          </div>
          <div className="space-y-3">
            {todayJobs.length === 0 && (
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg text-green-700">
                <FaCheckCircle className="shrink-0" />
                <p className="text-sm">No open jobs right now. You're all caught up!</p>
              </div>
            )}
            {todayJobs.map((job) => {
              const progress = job.steps_total > 0
                ? Math.round((job.steps_completed / job.steps_total) * 100)
                : 0;
              const isOverdue = (job.date || '').slice(0, 10) < todayStr;
              return (
                <div
                  key={job.id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition"
                  onClick={() => navigate(`/jobs/execute/${job.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">
                        {job.job_template?.name || 'Job'}
                      </span>
                      <Badge
                        variant={job.status === 'in_progress' ? 'info' : 'default'}
                        size="sm"
                      >
                        {job.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                      </Badge>
                      {isOverdue && (
                        <Badge variant="danger" size="sm">
                          Overdue · {(job.date || '').slice(0, 10)}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {job.steps_completed}/{job.steps_total} steps
                      </span>
                    </div>
                  </div>
                  <Button size="sm" variant={job.status === 'in_progress' ? 'primary' : 'secondary'}>
                    {job.status === 'in_progress' ? (
                      <><FaPlay className="mr-1" size={10} />Continue</>
                    ) : (
                      <><FaPlay className="mr-1" size={10} />Start</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Scan a label — visible to every authed user; primary shortcut for
          field staff who need to reach /scan without a physical camera read */}
      <Card
        onClick={() => navigate('/scan')}
        className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-indigo-500"
      >
        <div className="flex items-center gap-4">
          <div className="bg-indigo-100 p-3 rounded-lg shrink-0">
            <FaQrcode size={28} className="text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">Scan a label</p>
            <p className="text-sm text-gray-500">
              Point your camera at a QR/NFC label, or type the code manually.
            </p>
          </div>
          <span className="text-indigo-600 font-medium text-sm whitespace-nowrap">Open →</span>
        </div>
      </Card>

      {/* Today's Schedule — fan-out instances scoped to the current user (and
          further narrowed by the manager's site scope, when set) */}
      <TodayScheduleCard siteIdsParam={scope.siteIdsParam} />

      {/* 7-day density — shows when work is concentrated (and where the cliffs are) */}
      <WeekDensityCard siteIdsParam={scope.siteIdsParam} />

      {/* Stats Grid — managers/admins only. Each tile is a real link (not just
          an onClick div) so it's diggable — drills into the filtered list
          that produced the count, carrying the current site scope. */}
      {isMgr && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {visibleStatCards.map((stat) => (
          <Link key={stat.title} to={stat.link} aria-label={stat.title} className="block">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-4 rounded-lg`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>}

      {/* Activity Cards — managers/admins only */}
      {isMgr && <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submissions This Month */}
        <Card title="Submissions This Month">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {dashboardData?.submissions_this_month || 0}
                </p>
                <p className="text-sm text-gray-600 mt-1">Total submissions</p>
              </div>
              {(dashboardData.submissions_this_month > 0 || dashboardData.submissions_last_month > 0) && (
              <div
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  submissionChange >= 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {submissionChange >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                {Math.abs(submissionChange).toFixed(1)}%
              </div>
            )}
            </div>
            <p className="text-sm text-gray-600">
              Compared to {dashboardData?.submissions_last_month || 0} last month
            </p>
          </div>
        </Card>

        {/* Pending Approvals */}
        <Card title="Pending Approvals">
          <div className="space-y-4">
            <div>
              <p className="text-3xl font-bold text-gray-900">{dashboardData?.pending_approvals || 0}</p>
              <p className="text-sm text-gray-600 mt-1">Submissions waiting for review</p>
            </div>
            {dashboardData && dashboardData.pending_approvals > 0 && (
              <Button variant="secondary" fullWidth onClick={() => navigate('/approvals')}>
                Review Now
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Submissions */}
      <Card title="Recent Submissions">
        {dashboardData?.recent_submissions && dashboardData.recent_submissions.length > 0 ? (
          <div className="space-y-4">
            {dashboardData.recent_submissions.map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => navigate(`/submissions/${submission.id}`)}
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {submission.form_template?.name || 'Untitled Form'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Submitted by {submission.user?.first_name} {submission.user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(submission.submitted_at).toLocaleString()}
                  </p>
                </div>
                <Badge
                  variant={
                    submission.status === 'approved'
                      ? 'success'
                      : submission.status === 'rejected'
                      ? 'danger'
                      : submission.status === 'submitted'
                      ? 'warning'
                      : 'default'
                  }
                >
                  {submission.status}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-8">No recent submissions</p>
        )}
      </Card>

      {/* Popular Forms */}
      {dashboardData?.popular_forms && dashboardData.popular_forms.length > 0 && (
        <Card title="Popular Forms">
          <div className="space-y-3">
            {dashboardData.popular_forms.map((form, index) => (
              <div
                key={form.form_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                    {index + 1}
                  </div>
                  <p className="font-medium text-gray-900">{form.form_name}</p>
                </div>
                <Badge>{form.submission_count} submissions</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
      </>}
    </div>
  );
};

// TodayScheduleCard — surface today's open form-assignment instances at the
// top of the dashboard. Visible to every role; scoped per-user via the
// backend's standard visibility rules, and further narrowed by the manager's
// site scope (siteIdsParam) when one is set.
const TodayScheduleCard: React.FC<{ siteIdsParam?: string }> = ({ siteIdsParam }) => {
  const navigate = useNavigate();
  const http = useHttp();
  const { user } = useAuth();
  const canManage = isAdmin(user?.role) || isManager(user?.role);

  const [counts, setCounts] = useState<{
    total: number;
    overdue: number;
    pending: number;
    in_progress: number;
    completed: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = new Date();
        const ymd = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${dd}`;
        };
        const params: Record<string, string> = {
          from: ymd(today),
          to: ymd(today),
          limit: '500',
        };
        // Send the role ENUM LABEL (role_string), never the numeric enum — the
        // backend matches fa.assigned_to_role against the user_role enum, and a
        // numeric like "2" would fail the enum comparison. Mirrors Schedule.tsx.
        if (canManage && user?.role_string) params.role = user.role_string;
        if (siteIdsParam) params.site_ids = siteIdsParam;
        const resp = await http.get<any>('/form-assignment-instances', { params });
        const list = resp.data?.instances || [];
        const c = { total: list.length, overdue: 0, pending: 0, in_progress: 0, completed: 0 };
        for (const r of list) {
          if (r.status === 'overdue') c.overdue++;
          else if (r.status === 'pending') c.pending++;
          else if (r.status === 'in_progress') c.in_progress++;
          else if (r.status === 'completed') c.completed++;
        }
        if (!cancelled) setCounts(c);
      } catch {
        if (!cancelled) setCounts(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, user?.role, canManage, siteIdsParam]);

  if (loading) return null;
  if (!counts || counts.total === 0) return null;

  const open = counts.overdue + counts.pending + counts.in_progress;
  // Carry the current site scope into the calendar drill-down link so the
  // filtered view a manager is looking at here stays consistent there.
  const scopeQuery = siteIdsParam ? `&site_ids=${encodeURIComponent(siteIdsParam)}` : '';
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
        <button
          onClick={() => navigate('/calendar')}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Open calendar →
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Open" value={open} tone="primary" to={`/calendar?status=open${scopeQuery}`} />
        <Tile label="Overdue" value={counts.overdue} tone="danger" to={`/calendar?status=overdue${scopeQuery}`} />
        <Tile label="In progress" value={counts.in_progress} tone="warning" to={`/calendar?status=in_progress${scopeQuery}`} />
        <Tile label="Done today" value={counts.completed} tone="success" to={`/calendar?status=completed${scopeQuery}`} />
      </div>
      {counts.overdue > 0 && (
        <p className="mt-3 text-xs text-red-700">
          {counts.overdue} item{counts.overdue === 1 ? '' : 's'} overdue — review the calendar to prioritise.
        </p>
      )}
    </Card>
  );
};

// WeekDensityCard — 7-day bar chart of fan-out instance counts, segmented
// by status. Hidden when there's nothing to show. Stacked bars show a quick
// "where's the load this week" picture without leaving the dashboard.
const WeekDensityCard: React.FC<{ siteIdsParam?: string }> = ({ siteIdsParam }) => {
  const navigate = useNavigate();
  const http = useHttp();
  const { user } = useAuth();
  const canManage = isAdmin(user?.role) || isManager(user?.role);

  const [days, setDays] = useState<{
    date: string;
    label: string;
    pending: number;
    in_progress: number;
    completed: number;
    overdue: number;
  }[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = new Date();
        const ymd = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${dd}`;
        };
        const last = new Date(today);
        last.setDate(last.getDate() + 6);
        const params: Record<string, string> = {
          from: ymd(today),
          to: ymd(last),
          limit: '2000',
        };
        // Send the role ENUM LABEL (role_string), never the numeric enum — the
        // backend matches fa.assigned_to_role against the user_role enum, and a
        // numeric like "2" would fail the enum comparison. Mirrors Schedule.tsx.
        if (canManage && user?.role_string) params.role = user.role_string;
        if (siteIdsParam) params.site_ids = siteIdsParam;
        const resp = await http.get<any>('/form-assignment-instances', { params });
        const items = resp.data?.instances || [];
        const buckets = new Map<string, { pending: number; in_progress: number; completed: number; overdue: number }>();
        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          buckets.set(ymd(d), { pending: 0, in_progress: 0, completed: 0, overdue: 0 });
        }
        for (const r of items) {
          const k = (r.occurrence_date || '').slice(0, 10);
          const b = buckets.get(k);
          if (!b) continue;
          if (r.status === 'pending') b.pending++;
          else if (r.status === 'in_progress') b.in_progress++;
          else if (r.status === 'completed') b.completed++;
          else if (r.status === 'overdue') b.overdue++;
        }
        const out = Array.from(buckets.entries()).map(([date, c]) => {
          const d = new Date(date);
          const label = d.toLocaleDateString(undefined, { weekday: 'short' });
          return { date, label, ...c };
        });
        if (!cancelled) setDays(out);
      } catch {
        if (!cancelled) setDays(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, user?.role, canManage, siteIdsParam]);

  if (!days) return null;
  const max = Math.max(1, ...days.map((d) => d.pending + d.in_progress + d.completed + d.overdue));
  const total = days.reduce((acc, d) => acc + d.pending + d.in_progress + d.completed + d.overdue, 0);
  if (total === 0) return null;

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Next 7 days</h2>
        <button
          onClick={() => navigate('/calendar')}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          Open calendar →
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2 items-end" style={{ minHeight: '120px' }}>
        {days.map((d) => {
          const tot = d.pending + d.in_progress + d.completed + d.overdue;
          const h = (n: number) => (tot === 0 ? 0 : (n / max) * 100);
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => navigate('/calendar')}
              className="flex flex-col items-center justify-end gap-1 group"
              title={`${d.date}: ${tot} item${tot === 1 ? '' : 's'}`}
            >
              <span className="text-xs font-medium text-gray-700 mb-1">{tot || ''}</span>
              <div className="w-full flex flex-col-reverse" style={{ height: '88px' }}>
                {d.overdue > 0 && (
                  <div className="bg-red-400 group-hover:bg-red-500 transition-colors" style={{ height: `${h(d.overdue)}%` }} />
                )}
                {d.in_progress > 0 && (
                  <div className="bg-amber-400 group-hover:bg-amber-500 transition-colors" style={{ height: `${h(d.in_progress)}%` }} />
                )}
                {d.pending > 0 && (
                  <div className="bg-blue-400 group-hover:bg-blue-500 transition-colors" style={{ height: `${h(d.pending)}%` }} />
                )}
                {d.completed > 0 && (
                  <div className="bg-green-400 group-hover:bg-green-500 transition-colors" style={{ height: `${h(d.completed)}%` }} />
                )}
              </div>
              <span className="text-xs text-gray-500 mt-1">{d.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500">
        <Legend color="bg-red-400" label="Overdue" />
        <Legend color="bg-amber-400" label="Active" />
        <Legend color="bg-blue-400" label="Pending" />
        <Legend color="bg-green-400" label="Done" />
      </div>
    </Card>
  );
};

const Legend: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`inline-block w-3 h-3 rounded-xs ${color}`} />
    {label}
  </span>
);

// Tile — a stat tile inside Today's Schedule. When `to` is provided it's a
// drill-down link into the calendar (scoped by the caller); otherwise it
// stays a plain, non-interactive tile. Visual styling is identical either
// way — only the wrapping element and its accessible name change.
const Tile: React.FC<{
  label: string;
  value: number;
  tone: 'primary' | 'danger' | 'warning' | 'success';
  to?: string;
}> = ({ label, value, tone, to }) => {
  const palettes = {
    primary: 'bg-blue-50 text-blue-700 border-blue-200',
    danger: 'bg-red-50 text-red-700 border-red-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    success: 'bg-green-50 text-green-700 border-green-200',
  };
  // "block" is a no-op on the div (already block-level) but required on the
  // anchor so the Link fills the grid cell exactly like the div did.
  const className = `block border rounded-lg p-3 ${palettes[tone]}`;
  const content = (
    <>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide font-medium opacity-80">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link to={to} aria-label={`${value} ${label}`} className={className}>
        {content}
      </Link>
    );
  }
  return <div className={className}>{content}</div>;
};

export default Dashboard;
